import z from 'zod';
export const pageUpdatedWebhookSchema = z.looseObject({
    type: z.literal('page-updated'),
    projectId: z.string(),
    documentIds: z.array(z.string()),
    pageId: z.string(),
    correlationId: z.string().optional(),
    category: z.string().nullable(),
    userSuggestedCategory: z.string().nullable(),
    classification: z.array(z.tuple([z.string(), z.number()])),
    bytes: z.number(),
    metadata: z.looseObject({
        esn: z.string().optional(),
        tsn: z.string().optional(),
        csn: z.string().optional(),
        /**
         * Explicit document/report/header engine clocks (`PageMetadata.engineTsn` / `engineCsn`,
         * added in 0.169.0). Emitted as numbers — unlike the part-clock `tsn`/`csn` above, which
         * are stringified at the payload boundary.
         */
        engineTsn: z.number().optional(),
        engineCsn: z.number().optional(),
        date: z.string().optional(),
        title: z.string().optional(),
        engineType: z.string().optional(),
        thrustRating: z.string().optional(),
        /** Always percent; consumers must not infer another unit. */
        takeOffDeratePercent: z.number().optional(),
        /** EGT or TGT margin; always degrees Celsius; consumers must not infer another unit. */
        egtTgtMarginCelsius: z.number().optional(),
        /**
         * DR-4560: the basis of `egtTgtMarginCelsius` as printed on the chart it was read from —
         * "Worst Case" or "Sea Level". Emitted only together with `egtTgtMarginCelsius`; absent
         * when the chart prints no basis.
         */
        egtTgtMarginBasis: z.enum(['WORST_CASE', 'SEA_LEVEL']).optional(),
        /**
         * DR-3878: ISO `YYYY-MM-DD` estimate of the last plotted EGT/TGT margin point on an
         * ECM trend chart — an interpolation off the chart's x-axis, not a printed date, for
         * ranking undated ECM pages by freshness. Emitted only with the two readings above.
         */
        ecmTrendEndDate: z.string().optional(),
        /**
         * DR-4408: take-off temperature margin from a Test Cell Report page; always degrees
         * Celsius. Separate from the ECM `egtTgtMarginCelsius`. Emitted only together with
         * `testCellMarginParameter`.
         */
        testCellMarginCelsius: z.number().optional(),
        /** DR-4408: which temperature `testCellMarginCelsius` is on, as the page labels it. */
        testCellMarginParameter: z.enum(['EGT', 'ITT', 'TGT']).optional(),
        tslsv: z.number().optional(),
        cslsv: z.number().optional(),
        operator: z.string().optional(),
        /**
         * DR-4036: the party the page's work or report was performed FOR, verbatim from a
         * "Customer" / "Client" label. On shop-visit categories an `operator` equal to this
         * value is stripped, so the shop's customer arrives here and not as the operator.
         */
        customer: z.string().optional(),
        /**
         * DR-4244: shop-visit unit under work. Omitted on pages parsed before DR-4244 and when
         * unclear. Absence is not `engine`.
         */
        documentScope: z.enum(['engine', 'module', 'component']).optional(),
        aircraftRegistration: z.string().optional(),
        /** DR-3679: issuing authority of a status page, from visible evidence only. Omitted when unjudged. */
        sourceAuthority: z.enum(['operator-mro-signed', 'unknown']).optional(),
        /** DR-4364: release-certificate block 8 "Part No.". Omitted on pages that print no such block. */
        partNumber: z.string().optional(),
        /** DR-4364: release-certificate block 7 "Description", verbatim. */
        descriptionField: z.string().optional(),
        /**
         * DR-4364: release-certificate block 11/12 "Status/Work", e.g. NEW / MANUFACTURED /
         * OVERHAULED. NEW / MANUFACTURED marks an OEM manufacture certificate — the delivery
         * document that carries an engine's original licensed thrust.
         */
        statusWorkField: z.string().optional(),
    }),
    engineEvents: z
        .array(z.looseObject({
        type: z.string(),
        date: z.string(),
        thrust: z.string(),
        tsn: z.string(),
        csn: z.string(),
        reason: z.string(),
        operator: z.string(),
        aircraftReg: z.string(),
        position: z.string(),
    }))
        .optional(),
});
/**
 * `PageMetadata` keys deliberately never forwarded on the `page-updated` webhook —
 * pipeline-internal signals (classification/reconstruction hints, form-legibility
 * flags) or fields with no defined downstream consumer yet. Oil, the only consumer
 * of this webhook, should never see these. Adding a key here is a deliberate,
 * reviewed exclusion, not a place to quietly drop something that should ship — see
 * the exhaustiveness check below, which is what actually enforces that.
 */
export const PAGE_METADATA_INTERNAL_KEYS = [
    'type',
    'formFilled',
    'readable',
    'isSignaturePage',
    'pagination',
    // DR-3935: the companion of `partNumber` stays a reconstruction grouping hint, not a
    // wire field — `partNumber` itself ships since DR-4364.
    'previousPartNumbers',
    // DR-4364: a parse-time input to the classification write's `thrustRating` fill.
    'engineTypeThrustRating',
    'serialNumber',
    'buyer',
    'suggestedFilename',
    // DR-4684: the parser's logo/letterhead brand, kept as the audit trail for its operator strip.
    'preparedBy',
    // DR-4891: the persisted numeric date token and the document-level anchor provenance.
    'dateRawNumeric',
    'dateAnchoredBy',
];
const _pageMetadataKeysAreExhaustivelyMapped = true;
//# sourceMappingURL=page-updated.webhook.js.map